import React, { useState, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { buttons } from 'seal-ui-components';
import mixpanelEvents from '@Mixpanel';
import { sleep, getCurrentPage } from '@Utils/commonUtils';
import extractFilename from '@Utils/fileUtils';
import { cancelAnimation } from '@Actions/documentActions';
import { submitFileWithServiceCheck } from '@Actions/sealAPIActions';
import { NOTIFICATION_HANDLER } from '@Constants/types';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import store from '@Store';
import { STORAGE_TYPE, getFromStorage } from '@Service/storageService';
import { canAnalyzeDocument } from '@Service/authentication';
import { usePermissions } from '@Context';
import { calculateOverflow } from '@Utils/commonUtils';

/* global Office */

const SubmitForAnalysisButton = ({ history }) => {
    const dispatch = useDispatch();

    const [isSubmittingDocument, setIsSubmittingDocumentState] = useState(false);
    const [cancelled, setCancelledState] = useState(false);

    const { enabled: digAppsEnabled } = useSelector(state => state.scoreCard);
    const { digAppsSelectionInput } = useSelector(state => state.digAppsSelector);

    const isAnalyzing = useSelector(state => state.documentAnimation.run);

    const canNotSubmit = !canAnalyzeDocument();
    const disabled = isSubmittingDocument || cancelled || canNotSubmit;

    const { haveAccessToScorecardsPage } = usePermissions();

    const { id: digiAppId, text: digiAppName } = Object.values(digAppsSelectionInput).length
        ? Object.values(digAppsSelectionInput).filter(app => app.selected === true)[0]
        : { id: '', text: '' };

    const currentPage = getCurrentPage();

    useEffect(
        () => {
            if (cancelled) sleep(3000).then(() => setCancelledState(false));
        },
        [cancelled]
    );

    useEffect(
        () => {
            if (isSubmittingDocument) sleep(15000).then(() => setIsSubmittingDocumentState(false));
        },
        [isSubmittingDocument]
    );

    const submitDocument = () => {
        try {
            if (digAppsEnabled && haveAccessToScorecardsPage) {
                if (history.location !== '/scorecards') {
                    history.push('/scorecards');
                    calculateOverflow('submitDocument');
                }
            } else {
                if (history.location !== '/labels') {
                    history.push('/labels');
                    calculateOverflow('submitDocument');
                }
            }

            dispatch(submitFileWithServiceCheck(digiAppId, digiAppName, currentPage));
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'SubmitForAnalysisButton.submitDocument', err);
            dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    const onClickHandler = () => {
        if (canNotSubmit) {
            return dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload: 'You do not have sufficient access rights to Analyze the Document'
            });
        }
        const isMarkupApplied = getFromStorage('MarkupApplied', STORAGE_TYPE.PROPERTY_BAG);
        if (isMarkupApplied && isMarkupApplied === 'Yes') {
            const rootReducer = store.getState();
            const { document } = rootReducer;
            const { trackChangesEnabled } = document;

            if (trackChangesEnabled && trackChangesEnabled === true) {
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'Disable track changes and remove markup before submission'
                });
            } else {
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'Remove markup before submission'
                });
            }

            return; // prevent submission
        }

        Office.context.document.getFilePropertiesAsync(asyncResult => {
            const filename = extractFilename(asyncResult.value.url);
            if (filename && filename.length > 0) {
                if (isAnalyzing) {
                    const lastSubmissionFileSize = getFromStorage(
                        'lastSubmissionFileSize',
                        STORAGE_TYPE.PROPERTY_BAG
                    );

                    const docCancelAnalysisEventProperties = {
                        'Analyzer Display Mode': currentPage,
                        'Analyzer # size, nr of characters in contract': `${lastSubmissionFileSize} bytes`,
                        'Analyzer # Scorecard Id': digiAppId
                    };

                    cancelAnimation();
                    mixpanelEvents.docCancelAnalyzing(docCancelAnalysisEventProperties);

                    calculateOverflow('');
                    return setCancelledState(true);
                }

                setIsSubmittingDocumentState(true);
                return submitDocument();
            }

            // no filename, therefore end-user hasn't saved their document
            dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload: 'Document must be saved before submission.'
            });
            calculateOverflow('');

            return null;
        });
    };

    return (
        <buttons.Button
            data-testid={isAnalyzing ? 'analysis_cancel_button' : 'analyze_button'}
            disabled={disabled}
            appearance="primary"
            size="large"
            onClick={() => onClickHandler()}
            style={{ flex: 1, minWidth: '8rem' }}
        >
            {isSubmittingDocument ? 'Analyze' : isAnalyzing ? 'Cancel' : 'Analyze'}
        </buttons.Button>
    );
};

export default memo(SubmitForAnalysisButton);
